import useAuthenticatedClients from './../../hooks/useAuthenticatedClients';
import { queryKeys } from './../../constants';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { PriorityItemDto } from '../../api/api.generated';
import { onMutationError } from '../../utils/Utils';

export interface IReorderPrioritiesProps {
  employeeId: number;
  priorities: PriorityItemDto[];
}

function PriorityQueries() {
  const { getPrioritiesClient } = useAuthenticatedClients();
  const { PRIORITY_QUERY_KEY, EMPLOYEES_QUERY_KEY } = queryKeys;
  const queryClient = useQueryClient();

  /**
   * Fetches all priorities assigned to an team
   * @param teamId Id of the team to fetch priorities for
   * @param refetchInterval number of milliseconds before query refresh
   * @returns UseQueryResult<PriorityItemDto[], unknown>
   */
  const FetchPrioritiesByTeam = (
    includeProxies?: boolean,
    teamId?: number,
    startDate?: Date,
    endDate?: Date,
    roleName?: string[],
    refetchInterval?: number
  ) => {
    const result = useQuery(
      [PRIORITY_QUERY_KEY, null, includeProxies, teamId, startDate, endDate, roleName],
      async () => {
        return getPrioritiesClient().then(client =>
          client.getPrioritiesByTeam(includeProxies, teamId, startDate, endDate, roleName)
        );
      },
      { refetchInterval: refetchInterval ?? false }
    );
    return result;
  };

  const HasPermissionToEdit = (employeeId: number) => {
    const result = useQuery([PRIORITY_QUERY_KEY, employeeId], async () => {
      return getPrioritiesClient().then(client => client.hasPermissionToEdit(employeeId));
    });
    return result;
  };

  /**
   * Updates priorities & employees when a new priority is created
   */
  const { mutate: mutateCreatePriority } = useMutation<void, Error, PriorityItemDto>(createPriority, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(PRIORITY_QUERY_KEY);
      await queryClient.invalidateQueries(EMPLOYEES_QUERY_KEY);
      toast.success('Successfully created priority');
    },
    onError: onMutationError,
  });

  /**
   * Updates priorities & employees when an existing priority is updated
   */
  const { mutate: mutateUpdatePriority } = useMutation<void, Error, PriorityItemDto>(updatePriority, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(PRIORITY_QUERY_KEY);
      await queryClient.invalidateQueries(EMPLOYEES_QUERY_KEY);
      toast.success('Successfully updated priority');
    },
    onError: onMutationError,
  });

  /**
   * Updates priorities & employees when an existing priority is updated
   */
  const { mutate: mutateDeletePriority } = useMutation<void, Error, number>(deletePriority, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(PRIORITY_QUERY_KEY);
      await queryClient.invalidateQueries(EMPLOYEES_QUERY_KEY);
      toast.success('Successfully deleted priority');
    },
    onError: onMutationError,
  });

  /**
   * Updates priorities & employees when an existing priority is updated
   */
  const { mutate: mutateReorderPriorities } = useMutation<void, Error, IReorderPrioritiesProps>(reorderPriorities, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(PRIORITY_QUERY_KEY);
      toast.success('Successfully re-ordered priorities');
    },
    onError: onMutationError,
  });

  /**
   * Generates a POST request to create a new Priority
   * @param priority Data to create a new Priority
   */
  async function createPriority(priority: PriorityItemDto) {
    const client = await getPrioritiesClient();
    await client.createPriority(priority);
  }

  /**
   * Generates a POST request to create a new Priority
   * @param priority Data to create a new Priority
   */
  async function updatePriority(priority: PriorityItemDto) {
    const client = await getPrioritiesClient();
    await client.updatePriority(priority);
  }

  /**
   * Generates a DELETE request to delete an existing priority
   * @param id priority id
   */
  async function deletePriority(id: number) {
    const client = await getPrioritiesClient();
    await client.deletePriority(id);
  }

  /**
   * Generates a PUT request to reorder Priorities
   * @param props
   */
  async function reorderPriorities(props: IReorderPrioritiesProps) {
    const { employeeId, priorities } = props;
    const client = await getPrioritiesClient();
    await client.reorderPriorities(employeeId, priorities);
  }

  return {
    FetchPrioritiesByTeam,
    HasPermissionToEdit,
    mutateCreatePriority,
    mutateUpdatePriority,
    mutateDeletePriority,
    mutateReorderPriorities,
  };
}

export default PriorityQueries;
